import React, { useState } from 'react';
import { colors, type, space, radius, formatMoney } from '../tokens';

// Status → visual style for each dot
const STYLES = {
  Paid:      { bg: colors.success,     border: colors.success,     ring: false, label: 'Paid' },
  Scheduled: { bg: 'transparent',      border: colors.textTertiary, ring: false, label: 'Scheduled' },
  Failed:    { bg: colors.error,       border: colors.error,       ring: true,  label: 'Failed' },
  Paused:    { bg: colors.surfaceInk,  border: colors.textTertiary, ring: false, label: 'Paused', dashed: true },
  Skipped:   { bg: colors.textTertiary,border: colors.textTertiary, ring: false, label: 'Skipped' },
};

const monthLabel = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short' });
};

const ScheduleStrip = ({ installments = [] }) => {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const hovered = hoveredIdx != null ? installments[hoveredIdx] : null;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: space[2],
          padding: `${space[3]}px 0`,
          position: 'relative',
        }}
      >
        {installments.map((inst, i) => {
          const style = STYLES[inst.status] || STYLES.Scheduled;
          const active = hoveredIdx === i;
          return (
            <div
              key={i}
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, gap: 4, cursor: 'pointer' }}
            >
              <div
                title={`${inst.status} · ${formatMoney(inst.amount)} · ${new Date(inst.date).toLocaleDateString()}`}
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  background: style.bg,
                  border: `2px ${style.dashed ? 'dashed' : 'solid'} ${style.border}`,
                  boxShadow: active
                    ? `0 0 0 4px ${style.border}33`
                    : style.ring
                      ? `0 0 0 3px ${style.border}22`
                      : 'none',
                  transition: 'box-shadow 120ms',
                }}
              />
              <span style={{
                ...type.micro,
                color: active ? colors.textPrimary : colors.textTertiary,
                fontWeight: active ? 700 : 500,
              }}>
                {monthLabel(inst.date)}
              </span>
            </div>
          );
        })}
      </div>

      {hovered && (
        <div style={{
          marginTop: space[2],
          padding: space[3],
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: radius.sm,
          display: 'flex',
          gap: space[4],
          ...type.small,
        }}>
          <div>
            <div style={{ ...type.label, color: colors.textTertiary }}>Amount</div>
            <div style={{ ...type.body, fontWeight: 700, color: colors.textPrimary }}>
              {formatMoney(hovered.amount)}
            </div>
          </div>
          <div>
            <div style={{ ...type.label, color: colors.textTertiary }}>Date</div>
            <div style={{ ...type.body, color: colors.textPrimary }}>
              {new Date(hovered.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
          </div>
          <div>
            <div style={{ ...type.label, color: colors.textTertiary }}>Status</div>
            <div style={{
              ...type.body, fontWeight: 700,
              color: STYLES[hovered.status]?.border || colors.textPrimary,
            }}>
              {hovered.status}
            </div>
          </div>
          {hovered.method && (
            <div>
              <div style={{ ...type.label, color: colors.textTertiary }}>Method</div>
              <div style={{ ...type.body, color: colors.textPrimary }}>{hovered.method}</div>
            </div>
          )}
        </div>
      )}

      {/* Legend */}
      <div style={{
        display: 'flex',
        gap: space[4],
        marginTop: space[3],
        flexWrap: 'wrap',
        ...type.micro,
        color: colors.textTertiary,
      }}>
        {Object.entries(STYLES).map(([status, style]) => (
          <div key={status} style={{ display: 'flex', alignItems: 'center', gap: space[1] }}>
            <div style={{
              width: 10, height: 10, borderRadius: '50%',
              background: style.bg,
              border: `1.5px ${style.dashed ? 'dashed' : 'solid'} ${style.border}`,
            }} />
            {style.label}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ScheduleStrip;
